<template lang="pug">
.v-field-file.field-file(:class="field.className")
	template(v-if="!field.preview")
		transition(name="fade" )
			transition-group.field-file__list.flc(tag="div" name="content-fade" v-if="localFiles.length")
				.field-file__item(v-for="file in localFiles" :key="file.key")
					.field-file__item-start {{file.name}}
					.field-file__item-middle {{formatBytes(file.size)}}
					.field-file__item-end
						button.field-file__item-remove.link(v-if="field.remove" type="button" @click="remove(file)") {{ field.remove }}

		.field-file__main
			.field-file__cell-input
				.field-file__input-block
					label.field-label(v-if="field.label && field.mode !== 'no-label'" :for="uniqueId")
						| {{field.label}}
						span.field-text__star(v-if="field.required") *
					input.field-file__input(
						:id="uniqueId"
						type="file"
						:accept="acceptType"
						multiple="field.multiple"
						@change="fileInput"
						:key="rerenderKey"
						ref="input")
					button.field-file__btn.btn(v-if="field.add"
						type="button"
						:disabled="computedDisable"
						@click="openFileDialog"
						@keydown.enter="openFileDialog")
						| {{ field.add }}

			.field-file-preview__errors.flc(v-if="localErrors?.length")
				.error-list.flc
					.error-list__item.error-message.flc(v-for="(error, index) in localErrors" :key="index") {{ error }}

	template(v-if="field.preview")
		.field-file__main
			label.field-label(v-if="field.label && field.mode !== 'no-label'" :for="uniqueId")
				| {{field.label}}
				span.field-text__star(v-if="field.required") *
			input.field-file-preview__input(type="file" :accept="acceptType" @change="fileInput" multiple="field.multiple" :key="rerenderKey" ref="input" tabindex="-1")
			.field-file-preview__note(v-if="field.note" ) {{ field.note }}
			.field-file-preview__layout
				template(v-if="localFiles && localFiles.length" )
					button.field-file-preview__visual(
							v-for="(file, index) in localFiles"
							:key="file.key"
							type="button"
							tabindex="-1"
							:draggable="reorderEnabled"
							:class="{'is-dragging': dragIndex === index, 'is-dragover': dragOverIndex === index && dragIndex !== index}"
							@dragstart="onDragStart(index, $event)"
							@dragover.prevent="onDragOver(index)"
							@dragleave="onDragLeave(index)"
							@drop.prevent="onDrop(index)"
							@dragend="onDragEnd")
						img.field-file-preview__image(v-if="file?.path && field.typeContent === 'image'"
							:src="file.path" alt="")
						video.field-file-preview__image(v-else-if="file?.path && field.typeContent === 'video'"
							:src="file.path" alt="")
						button.field-file-preview__action-link--remove(
								@click="remove(file)")
							v-svg-icon.field-file-preview__action-icon(icon="delete")
						input.field-file-preview__action-link--main(
								v-if="field.typeContent === 'image'"
								:id="`action-main-${index}`"
								:checked="file.key === mainImageKey"
								ref="checkbox"
								type="checkbox"
								@change="checkMainImage(file.key)")
						label.field-file-preview__action-link-box(
								v-if="field.typeContent === 'image'"
								ref="checkboxView"
								:for="`action-main-${index}`")
							.field-file-preview__action-link-visual
							.field-file-preview__action-link-text(v-if="field.variables" ) {{ field.variables?.main || '' }}
				.field-file-preview__main
					.field-file-preview__actions
						.field-file-preview__action-link(
								@click="openFileDialog"
								@keydown.enter="openFileDialog"
								:class="{'disabled': computedDisable}")
							.field-file-preview__action-button
								v-svg-icon.field-file-preview__action-icon(icon="plus-icon")

			.field-file-preview__errors.flc(v-if="localErrors?.length")
				.error-list.flc
					.error-list__item.error-message.flc(v-for="(error, index) in localErrors" :key="index") {{ error }}
</template>

<script>
import uniqueId from '../scripts/helpers/uniqueId.js';
import formattingMixin from '../components/mixins/v-mixin-formatting.vue';
import controller from '../scripts/controller/controller';

export default {
	mixins: [
		formattingMixin
	],

	props: {
		note: {
			type: String,
			default: null
		},

		field: {
			type: Object,
			default: null
		},

		clientState: {
			type: Object,
			default: null
		},

		modelValue: {
			type: [Object, String, Number, Boolean],
			default: null
		},
	},
	
	emits: [
		'fileinput',
		'fileremove',
		'update:model-value'
	],

	data() {
		return {
			localFiles: [],
			localErrors: [],
			rerenderKey: 0,
			mainImageKey: null,
			dragIndex: null,
			dragOverIndex: null,
			inheritedLimitSignature: '',
			uniqueId: uniqueId('field-')
		};
	},

	watch: {
		field(newField, oldField) {
			if (newField !== oldField) {
				this.$nextTick(() => {
					this.syncInheritedFromField(newField?.files);
					this.updateGallery();
				});
			}
		},
		clientState(newState, oldState) {
			if (newState !== oldState) {
				this.bindClientState(newState);
			}
		},
		value() {
			this.initLocalValueFromValue();
		},
		// при автосабмите (смена проекта) сервер присылает новый набор наследованных
		// фото в field.files — подмёрдживаем их, сохраняя свои загруженные.
		'field.files': {
			deep: true,
			handler(files) {
				this.syncInheritedFromField(files);
			}
		},
		'field.stagedUploadError': {
			deep: true,
			handler(error) {
				if (!error?.key) return;
				const file = this.localFiles.find(item => item.key === error.key);
				if (file) {
					this.revokePreviewUrl(file);
					this.localFiles.splice(this.localFiles.indexOf(file), 1);
					this.updateGallery();
				}
				if (error.message) {
					this.localErrors = [error.message];
				}
			}
		}
	},

	created() {
		this.bindClientState(this.clientState);
	},

	mounted() {
		this.updateMainImage(this.$refs.checkbox, this.$refs.checkboxView)
	},

	updated() {
		this.updateMainImage(this.$refs.checkbox, this.$refs.checkboxView)
	},

	beforeUnmount() {
		// При автоперестроении формы компонент поля может временно размонтироваться.
		// Object URL принадлежат родительской форме и освобождаются при её закрытии.
		if (!this.clientState) {
			this.localFiles.forEach(this.revokePreviewUrl);
		}
	},

	methods: {
		bindClientState(state) {
			if (!state) {
				this.initLocalValueFromValue(true);
				return;
			}
			if (!Array.isArray(state.files)) state.files = [];
			this.localFiles = state.files;
			this.mainImageKey = state.mainImageKey || null;
			if (!state.initialized) {
				this.initLocalValueFromValue(true);
				state.initialized = true;
				this.syncInheritedFromField(this.field?.files);
			} else {
				this.syncInheritedFromField(this.field?.files);
				this.updateGallery();
			}
			this.syncClientState();
		},

		syncClientState() {
			if (!this.clientState) return;
			this.clientState.files = this.localFiles;
			this.clientState.mainImageKey = this.mainImageKey;
		},

		getKey() {
			return uniqueId();
		},

		updateMainImage(checkbox, checkboxView) {
			if(checkbox) {
				checkbox.forEach((checkbox, index) => {
					if (!checkbox.checked) {
						checkboxView[index].classList.add('hidden')
					}
				});

				if(checkbox.every(checkbox => !checkbox.checked)) {
					checkbox.forEach((checkbox, index) => {
						if (!checkbox.checked ) {
							checkboxView[index].classList.remove('hidden')
						}
					});
				}
			}
		},

		checkMainImage(key) {
			this.$refs.checkbox.forEach((checkbox, index) => {
				this.mainImageKey = key;
				if (!checkbox.checked) {
					this.$refs.checkboxView[index].classList.add('hidden')
				}
			});

			if(this.$refs.checkbox.every(checkbox => !checkbox.checked)) {
				this.$refs.checkbox.forEach((checkbox, index) => {
					this.mainImageKey = null;
					if (!checkbox.checked ) {
						this.$refs.checkboxView[index].classList.remove('hidden')
					}
				});
			}

			this.field.main = this.mainImageKey
			this.updateGallery();
		},

		openFileDialog() {
			if (this.computedDisable) {
				this.showValidationErrors([this.getClientError('count')]);
				return;
			}
			this.$refs.input?.click();
		},

		getFileExtension(file) {
			const name = String(file?.name || '');
			const dot = name.lastIndexOf('.');
			return dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
		},

		getClientError(type, file = null) {
			const validation = this.field?.fileValidation || {};
			const fallback = {
				size: 'Файл «#FILE#» превышает максимальный размер #MAX_SIZE#.',
				type: 'Формат файла «#FILE#» не поддерживается. Допустимые форматы: #FORMATS#.',
				count: 'Можно прикрепить не более #MAX_FILES# файлов.',
				empty: 'Файл «#FILE#» пуст и не был добавлен.'
			};
			let message = validation.messages?.[type] || fallback[type] || '';
			const maxSizeMb = Math.round(Number(validation.maxSize || this.field.maxSize || 0) / 1048576);
			const replacements = {
				'#FILE#': file?.name || '',
				'#MAX_SIZE#': `${maxSizeMb || 15} MB`,
				'#FORMATS#': validation.formats || (validation.extensions || []).join(', ').toUpperCase(),
				'#MAX_FILES#': validation.maxFiles || this.field.multipleLimit || 1
			};
			Object.entries(replacements).forEach(([token, value]) => {
				message = message.split(token).join(String(value));
			});
			return message;
		},

		showValidationErrors(errors, persistent = false) {
			const messages = [...new Set(errors.filter(Boolean))];
			this.localErrors = persistent ? messages : [];
			if (messages.length) {
				controller.addNotification({
					notificationText: messages.join(' ')
				});
			}
		},

		validateSelectedFiles(selectedFiles) {
			const validation = this.field?.fileValidation || {};
			const allowedExtensions = (validation.extensions || []).map(extension => String(extension).toLowerCase());
			const accepted = [];
			const errors = [];

			selectedFiles.forEach(file => {
				if (!file?.size) {
					errors.push(this.getClientError('empty', file));
					return;
				}
				if (this.field.maxSize && file.size > this.field.maxSize) {
					errors.push(this.getClientError('size', file));
					return;
				}
				if (allowedExtensions.length && !allowedExtensions.includes(this.getFileExtension(file))) {
					errors.push(this.getClientError('type', file));
					return;
				}
				if (!allowedExtensions.length
					&& this.field.typeContent
					&& !String(file.type || '').startsWith(`${this.field.typeContent}/`)) {
					errors.push(this.getClientError('type', file));
					return;
				}
				accepted.push(file);
			});

			const maxFiles = Number(validation.maxFiles || this.field.multipleLimit || (this.field.multiple ? 0 : 1));
			if (maxFiles > 0) {
				// Одиночное поле заменяет текущий файл, поэтому существующее значение
				// не уменьшает доступную ёмкость выбора.
				const existingCount = this.field.multiple ? (this.localFiles?.length || 0) : 0;
				const capacity = Math.max(0, maxFiles - existingCount);
				if (accepted.length > capacity) {
					accepted.splice(capacity);
					errors.push(this.getClientError('count'));
				}
			}

			return {files: accepted, errors};
		},

		fileInput(e) {
			const selectedFiles = Array.from(e.target.files || []);
			if (!selectedFiles.length) return;

			const validation = this.validateSelectedFiles(selectedFiles);
			this.showValidationErrors(validation.errors);
			const files = validation.files;
			if (!files.length) {
				this.rerenderKey = this.getKey();
				return;
			}

			if (!this.field.multiple) {
				this.localFiles.slice().forEach(this.remove);
			}

			let lastKey = null;
			files.forEach(file => {
				const key = this.getKey();
				lastKey = key;
				file.key = key;
				let path = null;
				let objectUrl = false;
				if (this.field.typeContent && typeof URL !== 'undefined' && URL.createObjectURL) {
					path = URL.createObjectURL(file);
					objectUrl = true;
				}
				this.localFiles.push({
					name: file.name,
					size: file.size,
					type: file.type,
					path,
					objectUrl,
					key
				});
			});

			this.$emit('fileinput', {
				key: lastKey,
				target: {files}
			});
			// Свежие файлы ещё не имеют серверных id, поэтому сразу фиксируем их
			// порядок клиентскими токенами k:<key>. Это позволяет менять порядок
			// до первого сохранения, в том числе в галерее без фото из проекта.
			this.updateGallery();
			this.rerenderKey = this.getKey();
		},

		remove(file) {
			this.localErrors = [];
			const index = this.localFiles.indexOf(file);
			if (index < 0) return;
			this.revokePreviewUrl(file);
			this.localFiles.splice(index, 1);
			// наследованное фото не эмитим как fileremove (оно не свой файл)
			if ((file.source || 'own') !== 'project') {
				this.$emit('fileremove', file);
			}
			this.updateGallery();
		},

		revokePreviewUrl(file) {
			if (file?.objectUrl && file.path && typeof URL !== 'undefined' && URL.revokeObjectURL) {
				URL.revokeObjectURL(file.path);
			}
		},

		updateLocalValue() {
			this.localValue = this.modelValue;
		},

		initLocalValueFromValue(initial) {
			if (this.field.files && initial) {
				this.localFiles.splice(0, this.localFiles.length);

				this.field.files.forEach((file) => {
					let newKey = this.getKey();

					if(this.field?.main) {
						this.mainImageKey = this.field.main
					}

					this.localFiles.push({
						name: file.name,
						size: file.size,
						path: file.path,
						type: file.type,
						key: file.key || newKey,
						source: file.source || 'own',
						fileId: file.fileId || null
					});

					if(!file.key) {
						file.key = newKey;
					}

					// наследованные от проекта фото НЕ загружаем как свои файлы
					if ((file.source || 'own') !== 'project') {
						this.$emit('fileinput', {
							key: file.key || newKey,
							target: {files: [file]}
						});
					}
				});

				this.updateGallery();
			}
			this.syncClientState();
		},

		// синхронизирует наследованные (source=project) фото из field.files в localFiles,
		// не теряя свои загруженные. Удаляет наследованные, которых больше нет (смена проекта).
		syncInheritedFromField(files) {
			if (!this.field || !this.field.inheritedSupported || !Array.isArray(files)) return;
			const allIncoming = files.filter(f => f.source === 'project' && f.fileId);
			const maxFiles = Number(this.field?.fileValidation?.maxFiles || this.field?.multipleLimit || 0);
			const ownCount = this.localFiles.filter(f => f.source !== 'project').length;
			const availableForInherited = maxFiles > 0 ? Math.max(0, maxFiles - ownCount) : allIncoming.length;
			const incoming = allIncoming.slice(0, availableForInherited);
			if (incoming.length < allIncoming.length) {
				const signature = `${ownCount}:${allIncoming.length}:${maxFiles}`;
				if (this.inheritedLimitSignature !== signature) {
					this.inheritedLimitSignature = signature;
					controller.addNotification({notificationText: this.getClientError('count')});
				}
			} else {
				this.inheritedLimitSignature = '';
			}
			const incomingIds = new Set(incoming.map(f => f.fileId));

			// убрать наследованные, которых нет в новом наборе
			const retained = this.localFiles.filter(f => f.source !== 'project' || incomingIds.has(f.fileId));
			this.localFiles.splice(0, this.localFiles.length, ...retained);

			// добавить новые наследованные
			const present = new Set(this.localFiles.filter(f => f.source === 'project').map(f => f.fileId));
			incoming.forEach(f => {
				if (!present.has(f.fileId)) {
					this.localFiles.push({
						name: f.name, size: f.size, path: f.path, type: f.type,
						key: this.getKey(), source: 'project', fileId: f.fileId
					});
				}
			});

			// главное по умолчанию — если ещё не выбрано, взять первое наследованное
			if (!this.mainImageKey && this.field.main) {
				const mf = this.localFiles.find(f => f.fileId && this.field.main && f.key === this.field.main);
				if (mf) this.mainImageKey = mf.key;
			}

			this.updateGallery();
		},

		// собирает галерею объявления (наследованные ссылки + главное + порядок) для сервера
		updateGallery() {
			this.syncClientState();
			if (!this.field || !this.field.galleryKey) return;
			const prj = this.localFiles
				.filter(f => f.source === 'project' && f.fileId)
				.map(f => f.fileId);
			let main = null;
			const mf = this.localFiles.find(f => f.key === this.mainImageKey);
			if (mf) {
				if (mf.fileId) main = { t: mf.source === 'project' ? 'prj' : 'own', id: mf.fileId };
				else main = { t: 'own', k: mf.key }; // свежезагруженное своё главное (ещё нет id)
			}
			// единый порядок показа: существующие — по fileId, свежезагруженные —
			// по клиентскому ключу "k:<key>" (сервер заменит их на новые id при сохранении).
			const order = this.localFiles.map(f => f.fileId ? f.fileId : ('k:' + f.key));
			this.field.galleryValue = { prj, main, order };
		},

		// ---- drag-and-drop переупорядочивание (Срез 2) ----
		onDragStart(index, e) {
			if (!this.reorderEnabled) return;
			this.dragIndex = index;
			if (e && e.dataTransfer) {
				e.dataTransfer.effectAllowed = 'move';
				// firefox требует setData для старта перетаскивания
				try { e.dataTransfer.setData('text/plain', String(index)); } catch (err) {}
			}
		},

		onDragOver(index) {
			if (!this.reorderEnabled || this.dragIndex === null) return;
			this.dragOverIndex = index;
		},

		onDragLeave(index) {
			if (this.dragOverIndex === index) this.dragOverIndex = null;
		},

		onDrop(index) {
			if (!this.reorderEnabled || this.dragIndex === null) return;
			const from = this.dragIndex;
			const to = index;
			if (from !== to && from >= 0 && to >= 0) {
				const moved = this.localFiles.splice(from, 1)[0];
				this.localFiles.splice(to, 0, moved);
				this.updateGallery();
			}
			this.dragIndex = null;
			this.dragOverIndex = null;
		},

		onDragEnd() {
			this.dragIndex = null;
			this.dragOverIndex = null;
		}
	},

	computed: {
		// accept для <input type=file>: на мобильных заставляет систему открывать
		// галерею изображений/видео (с источниками: камера, фото-приложения),
		// а не универсальный файловый пикер («Недавние»/документы).
		acceptType() {
			if (this.field.accept) {
				return this.field.accept;
			}
			if (this.field.typeContent === 'image') {
				return 'image/*';
			}
			if (this.field.typeContent === 'video') {
				return 'video/*';
			}
			return null;
		},

		computedDisable() {
			if(this.field.multipleLimit) {
				return this.localFiles?.length >= this.field.multipleLimit
			}
		},

		// Порядок можно менять во всех множественных фотогалереях ЛК, для которых
		// сервер объявил отдельный galleryKey. Наличие фото из проекта не требуется:
		// одинаково работают ручная загрузка, редактирование и автозаполнение.
		reorderEnabled() {
			return !!(this.field
				&& this.field.galleryKey
				&& this.field.typeContent === 'image'
				&& this.field.multiple
				&& this.localFiles.length >= 2);
		}
	}
};
</script>
